import { useCallback, useEffect, useState } from "react";

import { useApi, ApiError } from "../api/client";
import "./WhanauSurveyMobile.css";

/**
 * View/edit the exit-survey mobile for a whānau (ADR-008 §2.5).
 *
 * Shows the effective number (kaimahi override → Exess-synced) and lets a
 * keyworker set or clear an override. Consent comes from Exess and is shown
 * read-only; the SMS only sends when consent is yes and the master switch is on.
 * Strings are English for now — i18n / te reo is a follow-up.
 */

type ContactMobile = {
  mobile: string | null;
  source: "override" | "exess" | null;
  consent: boolean | null;
  smsCapable: boolean;
};

type Props = { whanauId: number };

export function WhanauSurveyMobile({ whanauId }: Props) {
  const callApi = useApi();
  const [data, setData] = useState<ContactMobile | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await callApi<ContactMobile>(
        `/whanau/${whanauId}/contact-mobile`,
      );
      setData(res);
      setValue(res.mobile ?? "");
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't load the mobile",
      );
    } finally {
      setLoading(false);
    }
    // callApi is a fresh closure each render; deliberately excluded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whanauId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(next: string | null) {
    setSaving(true);
    setError(null);
    try {
      const res = await callApi<ContactMobile>(
        `/whanau/${whanauId}/contact-mobile`,
        {
          method: "PUT",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ mobile: next }),
        },
      );
      setData(res);
      setValue(res.mobile ?? "");
      setEditing(false);
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 400
          ? "That doesn't look like a valid NZ mobile."
          : "Couldn't save. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="wsm__status">Loading…</p>;

  const consentLabel =
    data?.consent === true
      ? "Consent: yes"
      : data?.consent === false
        ? "Consent: no"
        : "Consent: unknown";

  return (
    <div className="wsm">
      <h3 className="wsm__title">Exit survey mobile</h3>

      {!editing ? (
        <div className="wsm__view">
          <span className="wsm__number">{data?.mobile ?? "Not on file"}</span>
          {data?.source && (
            <span className="wsm__source">
              {data.source === "override" ? "edited in WFP" : "from Exess"}
            </span>
          )}
          <button
            type="button"
            className="wsm__btn"
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
        </div>
      ) : (
        <div className="wsm__edit">
          <input
            type="tel"
            inputMode="tel"
            className="wsm__input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="021 234 5678"
            aria-label="Exit survey mobile number"
          />
          <button
            type="button"
            className="wsm__btn wsm__btn--primary"
            disabled={saving}
            onClick={() => void save(value.trim() ? value.trim() : null)}
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            className="wsm__btn"
            disabled={saving}
            onClick={() => {
              setEditing(false);
              setValue(data?.mobile ?? "");
              setError(null);
            }}
          >
            Cancel
          </button>
        </div>
      )}

      <p className="wsm__meta">
        {consentLabel} ·{" "}
        {data?.smsCapable ? "survey can be texted" : "won't be texted"}
      </p>

      {error && (
        <p className="wsm__error" role="alert">
          {error}
        </p>
      )}

      <p className="wsm__note">
        Edited here for the survey only. The source of truth is Exess.
      </p>
    </div>
  );
}

export default WhanauSurveyMobile;
